import DeviceManagerController from "./device_manager_controller"
import { regionStore } from "../stores/locales/region_store"
import { buildingStore } from "../stores/locales/building_store"
import { areaStore } from "../stores/locales/area_store"

/**
 * Area Device Manager Controller
 *
 * Extends DeviceManagerController to automatically reload devices when the
 * selected area changes. Ensures floor plans and tables show only devices
 * from the currently selected area.
 *
 * Use on pages with area selection dropdowns where device lists should
 * update automatically (e.g., Edit Areas page, Walkthrough page).
 *
 * TURBO DRIVE NOTE: The locale stores (regionStore, buildingStore, areaStore)
 * are module-level singletons that persist across Turbo navigations. Each
 * select controller (region/building/area-select) clears its own store in its
 * own disconnect(), and DeviceManagerController#disconnect() clears
 * allDevicesStore -- so stale selections and stale device lists from a
 * previous page are cleared as that page's controllers are torn down, before
 * this page's controllers ever connect. See region_select_controller.js for
 * the full rationale.
 *
 * Connects to data-controller="area-device-manager"
 */
export default class extends DeviceManagerController {

    static stores = [
        ...DeviceManagerController.stores,
        regionStore,
        buildingStore,
        areaStore
    ]

    static values = {
        ...DeviceManagerController.values,
        areaId: String
    }

    /**
     * React to area store changes and reload devices for the new area
     *
     * This override adds automatic device reloading when the area changes.
     *
     * Area Clearing Behavior:
     * When the area is cleared (set to null/undefined), this method clears the allDevicesStore
     * by setting it to an empty array. This ensures the floor plan shows no devices when
     * no area is selected.
     *
     * @param {Object|null} newArea - The selected area object or null if cleared
     */
    onAreaStoreUpdate(newArea) {
        if (newArea && newArea.id) {
            // Area selected: load devices for the new area
            this.loadDevices(newArea);
        } else if (this.allDevicesStoreValue && this.allDevicesStoreValue.length > 0) {
            // Area cleared: reset to empty state
            // Note: We check for existing devices to avoid unnecessary updates during initialization
            this.setAllDevicesStoreValue([]);
        }
    }

    handleAreaClearedEvent(event) {
        this.setAllDevicesStoreValue([]);
    }
}
